import { type FC, useMemo } from "react";
import { useBroadcastScore } from "../../../../hooks/use-broadcast-score";

import { useLineups } from "../../../../hooks/use-lineups";
import { useMatchMinute } from "../../../../hooks/use-match-minute";
import {
	toPlayerPhotoUrl,
	toProxiedMediaUrl,
} from "../../../../lib/media-proxy";
import { colours } from "../../../../styles/colours.constants";
import type { Post } from "../../../../types/post.types";
import PostFooter from "../../components/post-footer/post-footer";
import PostHeader from "../../components/post-header/post-header";
import PostText from "../../components/post-text/post-text";
import { usePostBroadcastContext } from "../../post-broadcast.context";
import * as CSS from "./card-post.styles";

type CardPostProps = {
	post: Post;
};

const CardPost: FC<CardPostProps> = ({ post }) => {
	const { homeTeam, homeLogo, awayTeam, awayLogo, broadcastId, fixtureId } =
		usePostBroadcastContext();

	const lineups = useLineups(fixtureId);

	const minute = useMatchMinute(post.date, post.minute);
	const { homeScore, awayScore } = useBroadcastScore(broadcastId);

	const player = useMemo(() => {
		if (!lineups || !post.team || post.playerId == null) return undefined;

		const teamLineup = lineups[post.team];
		return [...teamLineup.startXI, ...teamLineup.substitutes].find(
			(player) => player.id === post.playerId,
		);
	}, [lineups, post.team, post.playerId]);

	return (
		<CSS.Post $colour={post.cardType === "red" ? colours.red : colours.yellow}>
			<PostHeader
				time={minute}
				type={post.type}
				cardType={post.cardType}
				logo={
					post.team
						? toProxiedMediaUrl(post.team === "home" ? homeLogo : awayLogo)
						: undefined
				}
			/>
			<div
				style={{
					marginLeft: 44,
					display: "flex",
					flexDirection: "column",
					gap: "0.3rem",
				}}
			>
				<CSS.Title
					$colour={post.cardType === "red" ? colours.red : colours.yellow}
				>
					{post.cardType === "red" ? "Red card" : "Yellow card"}
				</CSS.Title>
				{player && (
					<CSS.PlayerRow>
						<img width="32" src={toPlayerPhotoUrl(player.id)} alt="" />
						<CSS.Text>
							{player.name} ({post.team === "home" ? homeTeam : awayTeam})
						</CSS.Text>
					</CSS.PlayerRow>
				)}
				<PostText postId={post.id} text={post.text} />
				<PostFooter postId={post.id} postType={post.type} />
			</div>
		</CSS.Post>
	);
};

export default CardPost;
